<!--
  The patient's media, the home of every layout: filter chips (all / images / videos / sets /
  progressions / notes & messages), date, tag, diagnosis and author filters, a search box, and a grid of cards.
  Typing a search switches the grid to results, across this patient, all the caregiver's patients,
  or the personal library. "Select" picks loose items to group into a set or to compare two.
  It only says what was picked; the layout decides where it opens. `compact` is the split view's
  narrow column, and `activeKey` marks the card open beside it.
-->
<template>
  <div class="md-lib" :class="{ 'is-compact': compact }">
    <div class="md-row md-lib-head">
      <div class="md-grow">
        <h2>{{ store.inLibrary ? 'My library' : 'Media' }}</h2>
        <span v-if="!compact" class="md-muted md-small">{{ store.inLibrary ? 'Photos and videos you took that are not in any chart. Only you see them.' : 'Photos and videos for this patient' }}</span>
      </div>
      <button v-if="store.canManage && !store.inLibrary && !compact" class="md-btn ghost" @click="$emit('capture', true)">+ Note with photos</button>
      <button v-if="store.canManage" class="md-btn" @click="$emit('capture', false)">+ Add media</button>
    </div>

    <MediaFilterBar v-model="filters" :counts="searching ? null : counts" :show-kinds="!searching" :show-notes="!store.inLibrary" :show-selects="filters.kind !== 'notes'" />

    <MediaSearchResults v-if="searching" :results="search.results" :total="search.total" :hint="search.hint" @open="openResult" />

    <MediaNotesFeed v-else-if="filters.kind === 'notes'" :active-key="activeKey" @open-note="(src, at) => $emit('open-note', src, at)" />

    <template v-else>
      <div v-if="store.canManage && looseShown.length" class="md-row md-lib-select">
        <template v-if="selecting">
          <strong class="md-small">{{ selected.length }} selected</strong>
          <button class="md-btn sm" :disabled="selected.length !== 2" @click="compareSelected">Compare (2)</button>
          <button class="md-btn ghost sm" :disabled="!selected.length" @click="groupSelected">Group into a set</button>
          <button class="md-link md-small" @click="stopSelecting">Cancel</button>
        </template>
        <button v-else class="md-link md-small" @click="selecting = true">Select items</button>
      </div>
      <p v-if="loading" class="md-muted">Loading…</p>
      <p v-else-if="!cards.length" class="md-muted">No media{{ anyFilter ? ' matches these filters' : ' yet' }}.</p>
      <div class="md-grid">
        <button v-for="c in cards" :key="c.key" class="md-tile" :class="{ selected: selected.includes(c.id) && c.type === 'item', active: c.key === activeKey, dim: selecting && c.type !== 'item' }"
          :disabled="selecting && c.type !== 'item'" @click="open(c)">
          <MediaThumb :item="c.cover" :count="c.count" :count-label="c.type === 'progression' ? 'sets' : 'items'" :show-visibility="store.canManage && c.type === 'item'" />
          <span v-if="selecting && c.type === 'item'" class="md-lib-check" :class="{ on: selected.includes(c.id) }">{{ selected.includes(c.id) ? '✓' : '' }}</span>
          <span class="meta">
            <span class="t">{{ c.title }}</span>
            <span class="md-muted">{{ c.sub }}</span>
            <span v-if="c.badge" class="md-lib-badge">{{ c.badge }}</span>
          </span>
          <span v-if="c.type === 'set' && store.canManage && !selecting" class="md-lib-edit" role="button" tabindex="0" title="Edit set" @click.stop="$emit('edit-set', c.id)" @keydown.enter.stop="$emit('edit-set', c.id)">⋯</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import Swal from 'sweetalert2';
import { useMediaStore } from '@/stores/mediaStore';
import { listPatientMedia, searchMedia } from '@/api/mediaApi';
import MediaThumb from './MediaThumb.vue';
import MediaFilterBar from './MediaFilterBar.vue';
import MediaSearchResults from './MediaSearchResults.vue';
import MediaNotesFeed from './MediaNotesFeed.vue';

defineProps({
  compact: { type: Boolean, default: false },
  activeKey: { type: String, default: '' },
});
const emit = defineEmits(['open-set', 'open-item', 'open-note', 'edit-set', 'capture', 'compare']);
const store = useMediaStore();
const filters = ref({ kind: 'all', text: '', scope: 'patient', date: '', date_from: '', date_to: '', primary_tag: '', author: '', orddiagid: '', sort: 'newest' });
const lib = ref({ items: [], sets: [], progressions: [] });
const search = ref({ results: [], total: 0, hint: null });
const loading = ref(false);
const selecting = ref(false);
const selected = ref([]);

const searching = computed(() => !!filters.value.text.trim());
const anyFilter = computed(() => !!(filters.value.date || filters.value.primary_tag || filters.value.author || filters.value.orddiagid));
const custom = () => filters.value.date === 'custom';
const dateFrom = () => (custom() ? filters.value.date_from
  : filters.value.date ? new Date(Date.now() - Number(filters.value.date) * 864e5).toISOString().slice(0, 10) : '');
// Diagnoses belong to one patient, so the filter only applies within that patient.
const diagnosisFilter = () => (searching.value && filters.value.scope !== 'patient' ? '' : filters.value.orddiagid);
const apiFilters = () => ({
  primary_tag: filters.value.primary_tag, author: filters.value.author, orddiagid: diagnosisFilter(),
  date_from: dateFrom(), date_to: custom() ? filters.value.date_to : '', sort: filters.value.sort,
});

// Only the latest request may fill the page: an answer to an earlier one (another patient, or the
// caregiver before "View as" switched) arrives late and is dropped.
let loadSeq = 0;
async function load() {
  const seq = ++loadSeq;
  loading.value = true;
  try {
    if (searching.value) {
      const scope = store.inLibrary && filters.value.scope === 'patient' ? 'mine' : filters.value.scope;
      const out = await searchMedia({ ...apiFilters(), text: filters.value.text, scope, patient_unique_id: store.patientUid });
      if (seq === loadSeq) search.value = out;
    } else {
      const out = await listPatientMedia(store.patientUid, apiFilters());
      if (seq === loadSeq) lib.value = out;
    }
  } catch (e) {
    if (seq !== loadSeq) return;
    // Never leave the previous viewer's results on screen.
    search.value = { results: [], total: 0, hint: null };
    lib.value = { items: [], sets: [], progressions: [] };
    store.say(e.message, 'error');
  } finally {
    if (seq === loadSeq) loading.value = false;
  }
}

const fmt = (d) => (d ? new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const inAnySet = computed(() => new Set(lib.value.sets.flatMap((s) => s.items.map((m) => m.media_id))));
const looseItems = computed(() => lib.value.items.filter((it) => !inAnySet.value.has(it.id)));

const itemCard = (it) => ({ key: `i${it.id}`, type: 'item', id: it.id, cover: it, count: 0, title: it.note || (it.kind === 'video' ? 'Video' : 'Image'), sub: fmt(it.captured_at), badge: null });
const setCard = (s) => ({ key: `s${s.id}`, type: 'set', id: s.id, cover: s.cover, count: s.count, title: s.name, sub: `${fmt(s.set_date)} · ${s.count} item${s.count === 1 ? '' : 's'}`, badge: s.progression_name ? `In “${s.progression_name}”` : null });
const progCard = (p) => ({ key: `p${p.id}`, type: 'progression', id: p.id, cover: p.cover, count: p.count, title: p.name, sub: `${p.count} sets · latest ${fmt(p.latest_date)}`, badge: 'Progression', latestSetId: p.sets.length ? p.sets[p.sets.length - 1].id : null });

const cards = computed(() => {
  const k = filters.value.kind;
  if (k === 'image' || k === 'video') return lib.value.items.filter((it) => it.kind === k).map(itemCard);
  if (k === 'set') return lib.value.sets.map(setCard);
  if (k === 'progression') return lib.value.progressions.map(progCard);
  // "All" shows each thing once: a set inside a progression is reached through the progression.
  return [...lib.value.progressions.map(progCard), ...lib.value.sets.filter((s) => !s.progression_id).map(setCard), ...looseItems.value.map(itemCard)];
});
const looseShown = computed(() => cards.value.filter((c) => c.type === 'item'));
const counts = computed(() => ({
  image: lib.value.items.filter((it) => it.kind === 'image').length,
  video: lib.value.items.filter((it) => it.kind === 'video').length,
  set: lib.value.sets.length,
  progression: lib.value.progressions.length,
}));

function open(c) {
  if (selecting.value) { toggle(c.id); return; }
  if (c.type === 'set') emit('open-set', c.id);
  else if (c.type === 'progression') { if (c.latestSetId) emit('open-set', c.latestSetId); }
  // An item steps through the cards it was opened from: images, videos, or the items in no set.
  else emit('open-item', c.id, filters.value.kind === 'image' || filters.value.kind === 'video' ? filters.value.kind : 'loose');
}
async function openResult(r) {
  if (r.type === 'set') emit('open-set', r.id);
  else if (r.type === 'item') emit('open-item', r.id);
  else {
    const p = await store.run((api) => api.getProgression(r.id));
    if (p && p.sets.length) emit('open-set', p.sets[p.sets.length - 1].id);
  }
}

function toggle(id) {
  const i = selected.value.indexOf(id);
  if (i >= 0) selected.value.splice(i, 1); else selected.value.push(id);
}
function stopSelecting() { selecting.value = false; selected.value = []; }
function compareSelected() { if (selected.value.length === 2) { emit('compare', selected.value[0], selected.value[1]); stopSelecting(); } }
async function groupSelected() {
  const res = await Swal.fire({ title: 'Group into a set', input: 'text', inputPlaceholder: 'Set name', showCancelButton: true, confirmButtonText: 'Create set', inputValidator: (v) => (!String(v || '').trim() ? 'Give the set a name.' : undefined) });
  if (!res.isConfirmed) return;
  const out = await store.run((api) => api.saveMediaSet({ patient_unique_id: store.patientUid, name: res.value, add_item_ids: [...selected.value] }), { success: 'Set created.', bump: true });
  if (out) { stopSelecting(); emit('open-set', out.id); }
}

let timer = null;
watch(filters, () => { stopSelecting(); clearTimeout(timer); timer = setTimeout(load, filters.value.text ? 250 : 0); }, { deep: true });
watch(() => store.patientUid, () => {
  if (filters.value.orddiagid) filters.value = { ...filters.value, orddiagid: '' };
});
watch(() => [store.patientUid, store.version], () => {
  // Cross-patient and personal-library scopes are caregiver only.
  if (!store.canManage && filters.value.scope !== 'patient') filters.value = { ...filters.value, scope: 'patient' };
  load();
}, { immediate: true });
</script>

<style scoped>
.md-lib-head { margin-bottom:14px; }
.md-lib-head h2 { font-size:24px; }
.md-lib-select { min-height:30px; margin-bottom:8px; }
.md-lib-badge { display:inline-block; margin-top:4px; font-size:11px; font-weight:600; color:var(--blue); background:var(--bluebg); border-radius:5px; padding:0 6px; }
.md-lib-edit { position:absolute; right:6px; top:6px; background:rgba(255,255,255,.9); width:28px; height:28px; border-radius:6px; display:flex; align-items:center; justify-content:center; color:var(--muted); font-size:18px; }
.md-lib-edit:hover { background:#eef4f4; color:var(--ink); }
.md-lib-check { position:absolute; top:8px; left:8px; width:24px; height:24px; border-radius:6px; border:2px solid #fff; background:rgba(0,0,0,.35); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; }
.md-lib-check.on { background:var(--teal); }
.md-tile.active { outline:3px solid var(--teal); }
.md-tile.dim { opacity:.45; cursor:not-allowed; }
.md-lib.is-compact .md-lib-head h2 { font-size:20px; }
.md-lib.is-compact :deep(.md-grid) { grid-template-columns:repeat(auto-fill, minmax(128px, 1fr)); gap:10px; }
</style>
